<template>
  <div>
    <div v-if="!hasReadableCompetitors" class="p-box">
      <div class="p-box__body">
        <GuideToSetUpCompetitor
          :is-more-than-editor="showMoreThanEditorAuthority"
        />
      </div>
    </div>
    <div v-else class="p-box p-box--competitors">
      <!-- start: 絞り込み条件モーダル -->
      <KkFilterModal
        :is-visible="isShowModal"
        :filter-items="filterItems"
        :form-values="formValues"
        :total-count="filteredCount"
        @close="isShowModal = false"
        @submit="handleResetPageAndSubmit"
        @reset="reset"
        @change-check-box="handleChangeFilterModalCheckBox"
      />
      <!-- end: 絞り込み条件モーダル -->
      <!-- start: 絞り込み条件選択 -->
      <form class="p-box__header">
        <div class="p-filtersA">
          <div class="p-filtersA__left">
            <div class="c-buttonFilter" @click="openModal">
              {{ $t('pages.CompaniesCompetitorsReviews.filters') }}
              <div class="c-buttonFilter__icon">
                <Icon name="iconFilter" />
              </div>
            </div>
          </div>
          <div class="p-filtersA__right">
            <form class="c-searchField" onsubmit="return false;">
              <input
                v-model="searchQuery"
                :disabled="loading"
                type="text"
                class="searchForm"
                :placeholder="$t('labels.search')"
                @keydown.enter="search"
              >
              <Icon name="iconSearch" />
            </form>
            <div class="sortForm c-buttonSort">
              <button
                class="c-buttonSort__item"
                :class="`is-${sortKey === 'posted_at' && sortOrder}`"
                type="button"
                :disabled="loading"
                @click="sort('posted_at')"
                v-text="$t('pages.CompaniesCompetitorsReviews.postedOrder')"
              />
              <button
                class="c-buttonSort__item"
                :class="`is-${sortKey === 'rating' && sortOrder}`"
                type="button"
                :disabled="loading"
                @click="sort('rating')"
                v-text="$t('pages.CompaniesCompetitorsReviews.ratingOrder')"
              />
            </div>
            <DatePicker
              v-if="period"
              :initial-dates="period"
              opens="left"
              @input="changePeriod"
            />
            <div v-if="canExportAuthority('reviews') && providedFeaturesCsvExport">
              <KkButton
                class="csvButton"
                color="secondary"
                height="38px"
                :font-bold="false"
                :text="$t('labels.csvDownload')"
                @click.prevent="clickCreateCsv"
              />
            </div>
          </div>
        </div>
      </form>
      <!-- end: 絞り込み条件選択 -->

      <div class="p-box__body">
        <Loading v-if="loading" />
        <template v-else>
          <template v-if="reviews">
            <!-- start: 口コミ一覧 -->
            <div v-if="reviews.data.length" class="p-reviews">
              <div class="p-reviews__total_count-competiter">
                {{ $t('pages.CompaniesCompetitorsReviews.totalCount', { number: numberLocaleString(reviews.meta.total_count) }) }}
              </div>
              <ReviewItem
                v-for="(review, i) in reviews.data"
                :key="i"
                :review="review"
                :search-query="$route.query['query']"
              />
            </div>
            <!-- end: 口コミ一覧 -->

            <!-- start: no data -->
            <div v-else class="noData" v-text="$t('messages.noData')" />
            <!-- end: no data -->

            <!-- start: ページネーション -->
            <div v-if="reviews.meta.total_pages" class="p-controller">
              <div class="p-controller__count p-controller__color_white">
                <p class="p-controller_p-controller__count p-controller__color_white_count__name">
                  {{ $t('labels.perPageCount') }}
                </p>
                <div class="p-controller__count__select">
                  <div class="p-controller__count__select__competitor">
                    <select :value="displayNumber" @change="changeDisplayNumber($event)">
                      <option v-for="item in 5" :key="item" :value="item * 50">{{ item * 50 }}</option>
                    </select>
                  </div>
                </div>
                <p class="p-controller__count__current">{{ startDataNumber }}-{{ endDataNumber }}</p>
              </div>
              <div class="p-controller__navi">
                <button
                  type="button"
                  :disabled="loading || currentPage === 1"
                  class="p-controller__navi__prev-competitor"
                  @click="goPrevPage"
                >
                  <Icon name="arrowLeft" />
                </button>
                <button
                  type="button"
                  :disabled="loading || currentPage >= reviews.meta.total_pages"
                  class="p-controller__navi__next-competitor"
                  @click="goNextPage"
                >
                  <Icon name="arrowRight" />
                </button>
              </div>
            </div>
            <!-- end: ページネーション -->
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex'
import DatePicker from '../components/DatePicker'
import { KkFilterModal } from '../components/projects'
import Loading from '../components/Loading.vue'
import Icon from '../components/Icon.vue'
import ReviewItem from '../components/CompaniesCompetitorsReviews/ReviewItem.vue'
import GuideToSetUpCompetitor from '../components/GuideToSetUpCompetitor'
import { toNumberLocaleString } from '../modules/localeString'
import {addAttributes, comments, replies} from '../modules/filterModalConst'
import { KkButton } from '../components/base'

export default {
  name: 'CompaniesCompetitorsReviews',
  components: {
    DatePicker,
    KkFilterModal,
    Loading,
    Icon,
    ReviewItem,
    GuideToSetUpCompetitor,
    KkButton,
  },
  data() {
    const companyUuid = this.$route.params.company_uuid
    return {
      companyUuid,
      isShowModal: false,
      searchQuery: this.$route.query['query'] || '',
      sortKey: null,
      sortOrder: null,
      currentPage: 1,
      filterItems: []
    }
  },
  computed: {
    ...mapState({
      reviews: state => state.companiesCompetitorsReviews.data,
      reviewsLoading: state => state.companiesCompetitorsReviews.loading,
      formValues: state => state.companiesCompetitorsReviews.formValues,
      filteredCount: state => state.companiesCompetitorsReviews.filteredCount,
      searchAttributes: state => state.companiesSearchTagGroups.data,
      searchAttributesLoading: state => state.companiesSearchTagGroups.loading,
      displayNumber: state => state.filters.displayNumber,
      period: state => state.filters.period,
    }),
    ...mapGetters({
      hasReadableCompetitors: 'companiesCompany/hasReadableCompetitors',
      canExportAuthority: 'companiesAccessibleApi/canExportAuthority',
      providedFeaturesCsvExport: 'companiesCompany/providedFeaturesCsvExport',
      showMoreThanEditorAuthority: 'companiesAccessibleApi/showMoreThanEditorAuthority',
      inboundOption: 'companiesCompany/inboundOption',
    }),
    ratings: () => gon.ratings,
    comments: () => comments,
    replies: () => replies,
    commonQuery() {
      return {
        posted_at_term: {
          start_date: this.period.startDate,
          end_date: this.period.endDate
        },
        store_uuids: this.formValues.stores,
        brand_ids: this.formValues.brands,
        competitor_ids: this.formValues.competitors,
        competitor_brand_ids: this.formValues.competitor_brands,
        site_types: this.formValues.sites,
        franchisee_ids: this.formValues.franchisees,
        user_company_ids: this.formValues.user_companies,
        area_ids: this.formValues.areas,
        prefectures: this.formValues.prefectures,
        ratings: this.formValues.ratings,
        comments: this.formValues.comments,
        replies: this.formValues.replies,
        locales: this.formValues.locales,
        original_tag_group_a_tag_ids: this.formValues.original_tag_group_a,
        original_tag_group_b_tag_ids: this.formValues.original_tag_group_b,
        original_tag_group_c_tag_ids: this.formValues.original_tag_group_c,
        original_tag_group_d_tag_ids: this.formValues.original_tag_group_d,
        original_tag_group_e_tag_ids: this.formValues.original_tag_group_e,
      }
    },
    loading() {
      return this.reviewsLoading || this.searchAttributesLoading
    },
    startDataNumber() {
      return (this.currentPage - 1) * this.displayNumber + 1
    },
    endDataNumber() {
      return this.startDataNumber + this.reviews.data.length - 1
    },
    currentCompanyUuid: function () {
      return this.$route.params.company_uuid
    },
    readCompetitorsReviewsParams() {
      return {
        company_uuid: this.companyUuid,
        values: {
          biz_companies_review_form: {
            ...this.commonQuery,
            page: this.currentPage,
            per: Number(this.displayNumber),
            sort_key: this.sortKey,
            sort_order: this.sortOrder,
          }
        }
      }
    }
  },
  watch: {
    $route(to) {
      if (!Object.keys(to.query).length) {
        this.initSearch()
      }
      this.handleResetPageAndSubmit()
    }
  },
  async created() {
    await this.readSearchAttributes(this.companyUuid)
    this.initData()
    const newFormValues = this.formInitialValues()
    const {
      analysis_type,
      analysis_id,
      cross_analysis_type,
      cross_analysis_id,
    } = this.$route.query
    if (analysis_type && analysis_id) {
      const id = ['stores', 'locales'].includes(analysis_type) ? analysis_id : Number(analysis_id)
      newFormValues[analysis_type].push(id)
    }
    if (cross_analysis_type && cross_analysis_id) {
      const id = ['stores', 'locales'].includes(cross_analysis_type) ? cross_analysis_id : Number(cross_analysis_id)
      newFormValues[cross_analysis_type].push(id)
    }
    this.setFormValues(newFormValues)
    this.createFilter()
    this.handleSubmit()
  },
  methods: {
    ...mapMutations({
      setFormValues: 'companiesCompetitorsReviews/setFormValues'
    }),
    ...mapActions({
      readCompetitorsReviews: 'companiesCompetitorsReviews/readCompetitorsReviews',
      readCompetitorsReviewsForFilter: 'companiesCompetitorsReviews/readCompetitorsReviewsForFilter',
      readSearchAttributes: 'companiesSearchTagGroups/readSearchTagGroups',
      createCompetitorsCsv: 'companiesCompetitorsReviews/createCompetitorsCsv',
      setDisplayNumber: 'filters/setDisplayNumber',
      setPeriod: 'filters/setPeriod',
    }),
    /**
     * 表示件数の変更
     * @param  { Object } e
     * @return { Void }
     */
    changeDisplayNumber(e) {
      this.setDisplayNumber(e.target.value)
    },
    /**
     * 期間の変更
     * @param  { Object } value
     * @return { Void }
     */
    changePeriod(value) {
      this.setPeriod(value)
      this.handleResetPageAndSubmit()
    },
    /**
     * 検索条件の初期化
     * @return { Void }
     */
    initData() {
      this.currentPage = 1
      this.sortKey = null
      this.sortOrder = null
    },
    /**
     * モーダルウィンドウの表示
     * @return { Void }
     */
    openModal() {
      if (this.loading) return
      this.isShowModal = true
    },
    /**
     * モーダルウィンドウの非表示
     * @return { Void }
     */
    closeModal() {
      this.isShowModal = false
    },
    /**
     * 絞り込み条件の初期値を返す
     */
    formInitialValues() {
      return {
        stores: [],
        brands: [],
        competitors: [],
        competitor_brands: [],
        sites: [],
        franchisees: [],
        user_companies: [],
        ratings: [],
        comments: [],
        replies: [],
        locales: [],
        areas: [],
        prefectures: [],
        original_tag_group_a: [],
        original_tag_group_b: [],
        original_tag_group_c: [],
        original_tag_group_d: [],
        original_tag_group_e: []
      }
    },
    /**
     * ソート
     * @param  { Object } key
     * @return { Void }
     */
    sort: function(key) {
      if (this.sortKey === key) {
        this.sortOrder = (this.sortOrder === 'desc' ) ? 'asc' : 'desc'
      } else {
        this.sortKey = key
        this.sortOrder = 'desc'
      }
      this.handleResetPageAndSubmit()
    },
    /**
     * 絞り込みのリセット
     * @return { Void }
     */
    reset() {
      this.setFormValues(this.formInitialValues())
      this.handleSubmit()
    },
    /**
     * 前のページへ
     * @return { Void }
     */
    goPrevPage() {
      --this.currentPage
      this.handleScrollTopAndSubmit()
    },
    /**
     * 次のページへ
     * @return { Void }
     */
    goNextPage() {
      ++this.currentPage
      this.handleScrollTopAndSubmit()
    },
    /**
     * 画面先頭へ移動しデータ更新
     * @return { Void }
     */
    handleScrollTopAndSubmit() {
      window.scrollTo({ top: 0 })
      this.handleSubmit()
    },
    initSearch() {
      this.searchQuery = ''
      this.initData()
    },
    /**
     * 検索キーワードの変更
     * @param  { Object } event
     * @return { Void }
     */
    search(event) {
      if (event.keyCode !== 13) return
      if (this.searchQuery === (this.$route.query['query'] || '')) return
      const query = { 'query': this.searchQuery || undefined }
      this.$router.push({ query })
    },
    /**
     * 検索条件の変更
     * @return { Void }
     */
    handleResetPageAndSubmit() {
      this.currentPage = 1
      this.handleSubmit()
    },
    async clickCreateCsv() {
      if (this.reviews.meta.total_count === 0) {
        this.$updateFlash({ type: 'alert', message: this.$t('pages.CompaniesExportResults.emptyData.review') })
        return
      }

      const payload = {
        company_uuid: this.currentCompanyUuid,
        values: {
          biz_companies_review_form: {
            ...this.commonQuery,
            per: this.reviews.meta.total_count,
            sort_key: this.sortKey,
            sort_order: this.sortOrder,
            query: this.searchQuery,
          }
        }
      }
      await this.createCompetitorsCsv(payload)
      this.$router.push({ name: 'CompaniesExportResults' })
    },
    /**
     * 検索の実行
     * @return { Void }
     */
    handleSubmit() {
      this.readCompetitorsReviews(this.readCompetitorsReviewsParams)
      this.datePickerOpened = false
      this.closeModal()
    },
    /**
     * 数値の3桁区切り
     * @param  { Number } value
     * @return { Number }
     */
    numberLocaleString(value) {
      return toNumberLocaleString(value)
    },
    handleChangeFilterModalCheckBox({ kind, id }) {
      const newFormValues = Object.assign({}, this.formValues)

      if(newFormValues[kind].includes(id)) {
        newFormValues[kind] = newFormValues[kind].filter((_id) => _id !== id)
      } else {
        newFormValues[kind].push(id)
      }

      this.setFormValues(newFormValues)
      this.readCompetitorsReviewsForFilter(this.readCompetitorsReviewsParams)
    },
    createFilter() {
      const attributes = [
        this.searchAttributes.comments,
        this.searchAttributes.replies,
        this.searchAttributes.ratings,
        this.inboundOption ? this.searchAttributes.locales : null,
        this.searchAttributes.sites,
        this.searchAttributes.brands,
        this.searchAttributes.areas,
        this.searchAttributes.franchisees,
        this.searchAttributes.user_companies,
        this.searchAttributes.competitor_brands,
      ].filter(v => v != null)

      if(this.searchAttributes.original_tag_group_a) attributes.push(this.searchAttributes.original_tag_group_a)
      if(this.searchAttributes.original_tag_group_b) attributes.push(this.searchAttributes.original_tag_group_b)
      if(this.searchAttributes.original_tag_group_c) attributes.push(this.searchAttributes.original_tag_group_c)
      if(this.searchAttributes.original_tag_group_d) attributes.push(this.searchAttributes.original_tag_group_d)
      if(this.searchAttributes.original_tag_group_e) attributes.push(this.searchAttributes.original_tag_group_e)

      attributes.push(this.searchAttributes.prefectures)
      attributes.push(this.searchAttributes.stores)
      attributes.push(this.searchAttributes.competitors)

      this.filterItems = attributes.map((attr) => {
        if(attr && attr.kind){
          return addAttributes(attr, attr.kind)
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../../stylesheets/biz_spa/foundation/_var.scss";

.searchForm {
  height: 38px;
  padding: 8px 6px 8px 48px;
}
.sortForm {
  margin-left: 16px !important;
}
.noData {
  color: $color-base-a
}
.csvButton {
  margin-left: 10px;
}
</style>
